import { useCallback } from "react";

import { useAdminContext } from "Bands/contexts";
import { requestCustomSubscription } from "Bands/state";
import {
  IBandSubscription,
  ICustomSubscriptionRequestParams,
  SubscriptionStatus,
  SubscriptionTypeWithTrial,
} from "Bands/types";
import { useDispatch } from "react-redux";
import { copy } from "shared/assets";
import { IActionDropdownItem } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { IStatus } from "shared/types";
import { mapUserPermissions } from "shared/utils";
const dashboardCopy = copy.adminDashboard;

export const useAdminSubscriptionActions = () => {
  const dispatch = useDispatch();

  const {
    setEditTrialModal,
    setStartTrialModalForBand,
    setEditCustomModal,
    setCancelPlanModal,
  } = useAdminContext();

  const getSubscriptionActions = useCallback(
    ({
      bandId,
      subscription,
    }: {
      bandId: string;
      subscription?: IBandSubscription;
    }) => {
      const actions: IActionDropdownItem[] = [];

      if (!subscription) return [];

      const isActive = subscription.status === SubscriptionStatus.active;
      const permissions = mapUserPermissions(subscription.permissions || []);

      switch (subscription.plan) {
        case SubscriptionTypeWithTrial.trial: {
          if (permissions.EDIT) {
            actions.push({
              label: dashboardCopy.editTrial.cta,
              handleClick: () => {
                setEditTrialModal(subscription);
              },
              dataTestId: "edit_trial_action",
            });
          }

          if (!isActive) {
            actions.push({
              label: dashboardCopy.startTrial.cta,
              handleClick: () => {
                setStartTrialModalForBand(bandId);
              },
              dataTestId: "start_trial_action",
            });
          }
          break;
        }
        case SubscriptionTypeWithTrial.custom: {
          if (isActive) {
            actions.push({
              label:
                dashboardCopy.customSubscriptions.actions
                  .createCustomSubscriptionRequest,
              handleClick: () => {
                const customSubscription: ICustomSubscriptionRequestParams = {
                  bands: [API_ENDPOINTS.BAND(subscription.bands[0].id!)],
                  company: subscription.company.id!,
                  createdBy: API_ENDPOINTS.COMPANY_USER(
                    subscription.createdBy.id
                  ),
                };

                dispatch(requestCustomSubscription(customSubscription));
              },
              dataTestId: "create_custom_subscription_action",
            });
          }

          if (permissions.EDIT) {
            actions.push({
              label: dashboardCopy.customSubscriptions.edit.title,
              handleClick: () => setEditCustomModal(subscription),
              dataTestId: "edit_subscriptions_action",
            });
          }
          break;
        }
      }

      if (permissions.CANCEL) {
        actions.push({
          label: dashboardCopy.cancelPlan.cta,
          handleClick: () => {
            setCancelPlanModal(subscription);
          },
          dataTestId: "cancel_plan_action",
          status: IStatus.error,
        });
      }

      return actions;
    },
    [
      dispatch,
      setCancelPlanModal,
      setEditCustomModal,
      setEditTrialModal,
      setStartTrialModalForBand,
    ]
  );

  return { getSubscriptionActions };
};
